import {Component,OnDestroy,OnInit} from '@angular/core';
import {ItemHierarchique,ItemHierarchiqueDetailType} from "@share/component/affichage-hierarchique/item-hierarchique";
import {BehaviorSubject,combineLatest,Observable,Subject,takeUntil} from "rxjs";
import {TypeColonnesAAfficher} from "@share/component/affichage-hierarchique/affichage-hierarchique.component";
import {CarteHierarchique} from "@share/component/affichage-hierarchique/carte-hierarchique";
import {CompteApiAutorisation} from "@domain/admin/parametre/api/compte-api-autorisation";
import {TypeCodeErreur} from "@domain/common/http/result";
import {TranslateService} from "@ngx-translate/core";
import {ToastrService} from "ngx-toastr";
import {ArbreHierarchique} from "@share/component/affichage-hierarchique/arbre-hierarchique";
import {ItemTitleComponent} from "@share/component/affichage-hierarchique/title-components/item-title/item-title.component";
import {CheckboxDetailComponent} from "@share/component/affichage-hierarchique/detail-components/checkbox-detail/checkbox-detail.component";
import {ApiRestDetailsService} from "../api-rest-details.service";
import {ApiInformation} from "@services/admin/parametre/api/api-rest.service";
import {CompteApi} from "@domain/admin/parametre/api/compte-api";
import {filterFirstNotNull} from "@share/utils/rxjs-custom-operator";

/**
 * Onglets "Droits" du détail 'un compte API Rest
 *
 * @author Laurent Convert
 * @date 26/11/2025
 */
@Component({
	host: {'data-test-id': 'api-rest-details-droits'},
	selector: 'api-rest-details-droits',
	templateUrl: './api-rest-details-droits.component.html',
	styleUrls: ['./api-rest-details-droits.component.scss'],
})
export class ApiRestDetailsDroitsComponent implements OnInit,OnDestroy {
	/** Compte API concerné */
	compteApi: CompteApi;

	/** Informations sur l'API */
	apiInformation: ApiInformation;

	/** Subject de destruction */
	private destroy$ = new Subject<void>();

	/** Liste des colonnes pour l'affichage hiérarchique */
	listeColonnes: Array<TypeColonnesAAfficher>;

	/** Carte contenant l'arbre des droits */
	carteDroits: CarteHierarchique;

	/** Arbre contenant la liste des droits */
	arbreDroits: ArbreHierarchique;

	/** Liste des droits */
	listeDroits: Array<ItemHierarchique>;

	/** Subject pour notifier si les données ont été chargées */
	private loaded: Subject<boolean> = new BehaviorSubject<boolean>(false);

	/** Observable indiquant si les données ont été chargées */
	loaded$: Observable<boolean> = this.loaded.asObservable();

	/**
	 * Constructeur
	 *
	 * @param translateService Service de traduction
	 * @param toastrService Service de notification
	 * @param apiRestDetailsService Service permettant de gérer les droits
	 */
	constructor(private translateService: TranslateService,
				private toastrService: ToastrService,
				private apiRestDetailsService: ApiRestDetailsService) {
	}

	/**
	 * Initialisation du composant
	 */
	ngOnInit(): void {
		//Chargement des informations sur l'API
		this.apiRestDetailsService.loadApiInformation();

		//Attente du chargement du compte et des informations sur l'API
		combineLatest([
			this.apiRestDetailsService.apiInformation$.pipe(filterFirstNotNull()),
			this.apiRestDetailsService.compteApi$.pipe(filterFirstNotNull())
		])
		.pipe(takeUntil(this.destroy$))
		.subscribe({
			next: ([apiInformation,compteApi]: [ApiInformation,CompteApi]) => {
				//Initialisation des données sur le composant pour une meilleure lisibilité
				this.apiInformation = apiInformation;
				this.compteApi = compteApi;

				//Initialisation de la liste des colonnes
				this.listeColonnes = [
					{id: DetailName.ALL,cleTraduction: 'admin.parametres.api.rest.details.droits.selectAll'},
					...this.apiInformation.listeHttpMethods.map(method => ({id: method,cleTraduction: method.toUpperCase()})),
				];

				//Construction de l'affichage des droits
				this.initDroitsRolesInHierarchie(this.compteApi.listeAutorisations);

				//Mise à jour de l'affichage des droits
				this.refreshArbre(this.compteApi.listeAutorisations);

				//Rafraichissement de l'affichage des droits à chaque enregistrement du compte
				this.apiRestDetailsService.compteApi$
					.pipe(takeUntil(this.destroy$))
					.subscribe({
						next: (compteApi: CompteApi) => {
							//Mise à jour des autorisations à partir du compte enregistré pour ne pas perdre les références des objets, sinon il faudrait reconstruire tout l'affichage
							compteApi.listeAutorisations.forEach((autorisation: CompteApiAutorisation) => {
								//Recherche de l'autorisation correspondante au path
								let compteApiAutorisation = this.compteApi.listeAutorisations.find(auth => auth.path == autorisation.path);

								//Mise à jour de l'autorisation si elle existe (ça devrait toujours être le cas)
								if (compteApiAutorisation != null) {
									Object.assign(compteApiAutorisation,autorisation);
								}
							});

							//Mise à jour du compte avec la référence de la liste des autorisations
							compteApi.listeAutorisations = this.compteApi.listeAutorisations;

							//Mise à jour de l'affichage des droits
							this.refreshArbre(this.compteApi.listeAutorisations);
						},
						error: () => {
							//Message d'erreur
							TypeCodeErreur.showError(TypeCodeErreur.ERROR_LOAD,this.translateService,this.toastrService);
						},
					});

				//Notification que les données ont été chargées et que le composant est initialisé
				this.loaded.next(true);
			},
			error: () => {
				//Message d'erreur
				TypeCodeErreur.showError(TypeCodeErreur.ERROR_LOAD,this.translateService,this.toastrService);
			},
		});
	}

	/**
	 * Initialise l'affichage des droits en fonction des informations du rôle
	 *
	 * @param listeAutorisations Liste des autorisations
	 */
	initDroitsRolesInHierarchie(listeAutorisations: Array<CompteApiAutorisation>) {
		//Initialisation de la liste des droits
		this.listeDroits = [];

		//Initialisation des branches
		this.arbreDroits = new ArbreHierarchique('admin.parametres.api.rest.details.droits.api',this.listeDroits);
		this.arbreDroits.avatar = 'api';
		this.arbreDroits.isFoldable = false;
		this.arbreDroits.folded = false;

		//Initialisation de l'arbre
		this.carteDroits = new CarteHierarchique('admin.parametres.api.rest.details.droits.title',[this.arbreDroits],'');
		this.carteDroits.folded = false;

		//Construction des branches à partir des endpoints disponibles retournés par le back
		Object.entries(this.apiInformation.mapAPI)
			//Tri sur le libellé (comme dans Swagger)
			.sort((a, b) => a[1].localeCompare(b[1]))
			.forEach(([path, libelle]) => {
				//Recherche de l'autorisation correspondante au path
				let compteApiAutorisation: CompteApiAutorisation = listeAutorisations.find(auth => auth.path == path);

				//Si l'autorisation n'est pas trouvée, on la crée (nouveau endpoint créé après l'enregistrement des droits du compte)
				if (compteApiAutorisation == null) {
					//Initialisation de l'autorisation
					compteApiAutorisation = new CompteApiAutorisation();
					compteApiAutorisation.path = path;

					//Désactivé par défaut pour toutes les méthodes HTTP
					for (const method of this.apiInformation.listeHttpMethods) {
						compteApiAutorisation.setAutorisation(method,false);
					}

					//Ajout de l'autorisation à la liste
					listeAutorisations.push(compteApiAutorisation);
				}

				//Construction de la branche pour le endpoint
				const branche: ItemHierarchique = new ItemHierarchique(
					ItemTitleComponent,
					path,
					{libelle: libelle},
					this.buildListeComposantsDetail(compteApiAutorisation),
					null
				);

				//Ajout de la branche à la liste
				this.listeDroits.push(branche);
			});
	}

	/**
	 * Construction de la branche qui correspond à un endpoint
	 *
	 * @param compteApiAutorisation Autorisation
	 */
	private buildListeComposantsDetail(compteApiAutorisation: CompteApiAutorisation): {
		composantRecord: Record<string,ItemHierarchiqueDetailType>,
		inputsRecord: Record<string,any>,
		allChecked$: Subject<boolean>,
	} {
		//Initialisation des composants de détail
		let composantRecord: Record<string,ItemHierarchiqueDetailType> = {};
		let inputsRecord: Record<string,any> = {};
		const subjectAll = new Subject<ItemHierarchique>();
		const allChecked$ = new Subject<boolean>();

		//Abonnement au subject de la case à cocher 'Tous'
		subjectAll.pipe(takeUntil(this.destroy$)).subscribe({
			next: (item) => {
				//Inversion du statut coché/décoché
				const checked = !inputsRecord[DetailName.ALL].checked;

				//Mise à jour de la case à cocher 'Tous'
				inputsRecord[DetailName.ALL].checked = checked;
				item.checked = checked;

				//Mise à jour de la case à cocher correspondant à chaque méthode HTTP
				for (const method of this.apiInformation.listeHttpMethods) {
					inputsRecord[method].checked = checked;
					compteApiAutorisation.setAutorisation(method,checked);
				}
			}
		});

		//Ajout de la case à cocher de la colonne 'Tous', cochée ou non en fonction des méthodes HTTP activées ou non
		composantRecord[DetailName.ALL] = CheckboxDetailComponent;
		inputsRecord[DetailName.ALL] = {
			subject: subjectAll,
			checked: false,
		};

		//Ajout d'une case à cocher pour chaque méthode HTTP
		for (const method of this.apiInformation.listeHttpMethods) {
			const subject = new Subject<ItemHierarchique>();
			subject.pipe(takeUntil(this.destroy$)).subscribe({
				next: (branche) => {
					//Inversion du statut coché/décoché pour la méthode HTTP modifiée et mise à jour de l'état
					inputsRecord[method].checked = !inputsRecord[method].checked;
					compteApiAutorisation.setAutorisation(method,inputsRecord[method].checked);

					//Détermine l'état global 'tous' en fonction des méthodes HTTP activées
					const allChecked = this.isAllChecked(compteApiAutorisation);

					//Mise à jour de la case à cocher 'Tous'
					inputsRecord[DetailName.ALL].checked = allChecked;
					branche.checked = allChecked;
				}
			});

			//Définition du composant case à cocher
			composantRecord[method] = CheckboxDetailComponent;

			//Définition des inputs associés à la case à cocher
			inputsRecord[method] = {subject: subject,checked: false};
		}

		//On renvoie la liste des composants de l'élément et leurs inputs
		return {composantRecord: composantRecord,inputsRecord: inputsRecord,allChecked$: allChecked$};
	}

	/**
	 * Met à jour l'affichage de l'arbre des autorisations
	 *
	 * @param listeAutorisations Liste des autorisations
	 */
	refreshArbre(listeAutorisations: Array<CompteApiAutorisation>) {
		this.listeDroits.forEach(item => {
			//Recherche l'autorisation dans l'arbre qui correspond au path
			const compteApiAutorisation = listeAutorisations.find(auth => auth.path == item.identifiant);

			//Si l'autorisation est trouvée, on met à jour l'affichage
			if (compteApiAutorisation != null) {
				//Mise à jour de la case à cocher 'Tous'
				item.listeInputsComposantsDetail[DetailName.ALL].checked = this.isAllChecked(compteApiAutorisation);

				//Mise à jour des cases à cocher pour chaque méthode HTTP
				for (const method of this.apiInformation.listeHttpMethods) {
					item.listeInputsComposantsDetail[method].checked = compteApiAutorisation?.hasAutorisation(method) === true;
				}
			}
		});
	}

	/**
	 * Vérifie si toutes les cases à cocher sont cochées
	 *
	 * @param compteApiAutorisation Branche/autorisation
	 */
	private isAllChecked(compteApiAutorisation: CompteApiAutorisation): boolean {
		return this.apiInformation.listeHttpMethods.every((method) => compteApiAutorisation.hasAutorisation(method) === true);
	}

	/**
	 * Destruction du composant
	 */
	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}

}

/** Énumération des noms de colonnes pour l'affichage des cartes hiérarchiques des droits */
enum DetailName {
	ALL = "ALL",
}